/**
 * PlatformImpactStrip — the role-scoped "the platform is scaling your work" surface.
 *
 * Names the Enable 2.0 transformation's scale value in the discovery-deck's own
 * numbers for the active role: an advisory headline (via AIInsightCard, so the
 * disclosure treatment matches every other AI surface), 3–4 headline metric
 * tiles, the automated/AI-assisted/manual work-mix bar, the 160→64 workflow-step
 * reduction, and the agentic capabilities active for the role. Advisory only —
 * a modeled productivity view, not a commitment. Rendered at the top of each
 * role landing page, above the work surface (see roleViews.ts). Data comes from
 * usePlatformImpact() which degrades to a deterministic seed when the endpoint
 * is absent (so the demo always renders).
 */
import type { CSSProperties } from 'react';
import { useNavigate } from 'react-router-dom';
import { colors, radius, tints } from '../theme';
import { AIInsightCard } from './AIInsightCard';
import { useAuth } from '../auth/useAuth';
import { usePlatformImpact, type ImpactMetric, type PlatformImpact } from '../hooks/useIntelligence';
import { useAiReview } from '../hooks/useAiReview';
import { isPathAllowedInView } from './roleViews';

export interface PlatformImpactStripProps {
  impact: PlatformImpact | null;
  /** When false (disclosure off / seed fallback), show the muted "representative" note. */
  available: boolean;
  /** Open HITL AI-review count; renders a "needs your review" pill linking to /ai-review. */
  reviewCount?: number;
  /** Navigate to the AI-review queue (wired by the host page). */
  onReview?: () => void;
  style?: CSSProperties;
}

/** Compact-format a metric value for display (currency / multiplier / percent / count). */
function formatValue(m: ImpactMetric): string {
  if (m.unit === '$') {
    const abs = Math.abs(m.value);
    if (abs >= 1_000_000) return `$${(m.value / 1_000_000).toFixed(1)}M`;
    if (abs >= 1_000) return `$${Math.round(m.value / 1_000)}K`;
    return `$${Math.round(m.value)}`;
  }
  if (m.unit === '×') return `${m.value}×`;
  if (m.unit === '%') return `${m.value > 0 ? '' : ''}${m.value}%`;
  return `${m.value}`;
}

/** The "vs baseline" delta line under a tile, when a baseline exists. */
function baselineLabel(m: ImpactMetric): string | null {
  if (m.baseline == null) return null;
  const b = m.unit === '×' ? `${m.baseline}×` : m.unit === '%' ? `${m.baseline}%` : `${m.baseline}`;
  return m.direction === 'down' ? `from ${b}` : `vs ${b}`;
}

export function PlatformImpactStrip({
  impact,
  available,
  reviewCount,
  onReview,
  style,
}: PlatformImpactStripProps) {
  if (!impact) return null;
  const { work_mix } = impact;

  return (
    <div style={{ ...wrap, ...style }}>
      <AIInsightCard heading={impact.headline} confidence={impact.confidence} readOnly>
        <div>{impact.subhead}</div>
        {!available && <div style={mutedNote}>Representative — pending live model.</div>}
      </AIInsightCard>

      <div style={metricsRow}>
        {impact.metrics.map((m) => {
          const delta = baselineLabel(m);
          return (
            <div key={m.key} style={tile}>
              <div style={tileValue}>{formatValue(m)}</div>
              <div style={tileLabel}>{m.label}</div>
              {delta && (
                <div style={{ ...tileDelta, color: colors.action }}>
                  <span aria-hidden>{m.direction === 'up' ? '↑' : '↓'}</span> {delta}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div style={mixBlock}>
        <div style={mixBar} aria-hidden>
          <div style={{ ...mixSeg, width: `${work_mix.automated_pct}%`, background: MIX_AUTOMATED }} />
          <div style={{ ...mixSeg, width: `${work_mix.ai_assisted_pct}%`, background: MIX_AI }} />
          <div style={{ ...mixSeg, width: `${work_mix.manual_pct}%`, background: MIX_MANUAL }} />
        </div>
        <div style={mixCaption}>
          Today's steps: <strong>{work_mix.automated_pct}% automated</strong> ·{' '}
          <strong>{work_mix.ai_assisted_pct}% AI-assisted</strong> · {work_mix.manual_pct}% manual
          <span style={stepsChip} title="Workflow steps: before → after">
            {impact.steps_before} → {impact.steps_after} steps
          </span>
        </div>
      </div>

      {(impact.capabilities.length > 0 || (reviewCount && reviewCount > 0)) && (
        <div style={capRow}>
          <span style={capLabel}>Active AI capabilities</span>
          {impact.capabilities.map((c) => (
            <span key={c} style={capChip}>
              {c}
            </span>
          ))}
          {reviewCount != null && reviewCount > 0 && (
            <button type="button" style={reviewPill} onClick={onReview} title="Open the AI review queue">
              ✦ Needs your review: {reviewCount}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * RoleImpactStrip — the drop-in the role landing pages use. Reads the active
 * role view, loads its platform-impact view, and surfaces the open HITL
 * AI-review count as a pill (only for views that can reach `/ai-review`).
 */
export function RoleImpactStrip({ style }: { style?: CSSProperties }) {
  const { activeView } = useAuth();
  const navigate = useNavigate();
  const { impact, available } = usePlatformImpact(activeView);
  const { tasks } = useAiReview();
  const canReview = isPathAllowedInView(activeView, '/ai-review');
  const openReview = tasks.filter((t) => t.status === 'open').length;
  return (
    <PlatformImpactStrip
      impact={impact}
      available={available}
      reviewCount={canReview ? openReview : undefined}
      onReview={() => navigate('/ai-review')}
      style={style}
    />
  );
}

// Work-mix bar colors — teal family (AI/action layer), manual in neutral gray.
const MIX_AUTOMATED = '#0F766E';
const MIX_AI = '#2DD4BF';
const MIX_MANUAL = colors.surfaceAlt;

const wrap: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
  marginBottom: 20,
};
const mutedNote: CSSProperties = { fontSize: '0.72rem', color: colors.textMuted, marginTop: 6 };
const metricsRow: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
  gap: 12,
};
const tile: CSSProperties = {
  background: colors.surface,
  border: `1px solid ${colors.border}`,
  borderRadius: radius.md,
  padding: '12px 14px',
};
const tileValue: CSSProperties = { fontSize: '1.5rem', fontWeight: 700, color: colors.textPrimary, lineHeight: 1.1 };
const tileLabel: CSSProperties = { fontSize: '0.8rem', color: colors.textSecondary, marginTop: 4 };
const tileDelta: CSSProperties = { fontSize: '0.74rem', fontWeight: 600, marginTop: 4 };
const mixBlock: CSSProperties = { display: 'flex', flexDirection: 'column', gap: 6 };
const mixBar: CSSProperties = {
  display: 'flex',
  height: 10,
  borderRadius: radius.pill,
  overflow: 'hidden',
  background: colors.surfaceAlt,
};
const mixSeg: CSSProperties = { height: '100%' };
const mixCaption: CSSProperties = {
  fontSize: '0.78rem',
  color: colors.textSecondary,
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: 8,
};
const stepsChip: CSSProperties = {
  display: 'inline-block',
  padding: '2px 10px',
  fontSize: '0.72rem',
  fontWeight: 700,
  borderRadius: radius.pill,
  background: tints.action,
  color: tints.actionText,
  marginLeft: 4,
};
const capRow: CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' };
const capLabel: CSSProperties = {
  fontSize: '0.68rem',
  fontWeight: 700,
  letterSpacing: '0.05em',
  textTransform: 'uppercase',
  color: colors.textMuted,
};
const capChip: CSSProperties = {
  display: 'inline-block',
  padding: '3px 10px',
  fontSize: '0.74rem',
  fontWeight: 600,
  borderRadius: radius.pill,
  background: tints.action,
  color: tints.actionText,
};
const reviewPill: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  padding: '3px 12px',
  fontSize: '0.74rem',
  fontWeight: 700,
  borderRadius: radius.pill,
  background: tints.warning,
  color: tints.warningText,
  border: 'none',
  cursor: 'pointer',
};
